/**
 * DeliveryHistoryDetailPage Component
 * 배달이력 상세 페이지 - VROONG 브랜드 통일 디자인
 */

import React from 'react';
import BottomNavigation from '../../../../core/components/BottomNavigation';
import { useDeliveryHistoryDetail } from '../hooks/useDeliveryHistoryDetail';
import styles from '../styles/DeliveryHistoryDetailPage.module.css';

const DeliveryHistoryDetailPage: React.FC = () => {
    const {
        detailList,
        isLoading,
        error,
        formatDate,
        formatAmount,
        handleBack,
        slotTitle,
        dateRangeText,
        totalDeliveryAmt,
    } = useDeliveryHistoryDetail();

    // 배달 상태에 따른 뱃지 스타일
    const getStatusBadgeClass = (status: string): string => {
        if (status === '완료' || status === 'COMPLETED') {
            return styles['completed'];
        }
        if (status === '거절' || status === 'REJECTED') {
            return styles['rejected'];
        }
        return '';
    };

    // 배달 상태 아이콘
    const getStatusIcon = (status: string): string => {
        if (status === '완료' || status === 'COMPLETED') {
            return '✅';
        }
        if (status === '거절' || status === 'REJECTED') {
            return '❌';
        }
        return '📋';
    };

    return (
        <div className={`screen active ${styles['detail-screen']}`}>
            {/* 헤더 */}
            <div className={styles['detail-header']}>
                <div className={styles['header-top']}>
                    <button className={styles['back-btn']} onClick={handleBack}>
                        <i className="fas fa-arrow-left"></i>
                    </button>
                    <div className={styles['header-content']}>
                        <h1 className={styles['page-title']}>{slotTitle} 상세 내역</h1>
                        {dateRangeText && (
                            <p className={styles['date-range']}>조회기간 : {dateRangeText}</p>
                        )}
                    </div>
                </div>
            </div>

            {/* 메인 컨텐츠 */}
            <div className={styles['detail-container']}>
                {isLoading ? (
                    <div className={styles['loading-section']}>
                        <div className={styles['loading-spinner']}></div>
                        <p className={styles['loading-text']}>상세 정보를 불러오는 중...</p>
                    </div>
                ) : error ? (
                    <div className={styles['error-section']}>
                        <div className={styles['error-icon']}>⚠️</div>
                        <p className={styles['error-text']}>{error}</p>
                    </div>
                ) : detailList.length === 0 ? (
                    <div className={styles['empty-section']}>
                        <div className={styles['empty-icon']}>📭</div>
                        <p className={styles['empty-text']}>조회된 배달이력 상세 내역이 없습니다.</p>
                    </div>
                ) : (
                    <>
                        {/* 합계 카드 */}
                        {totalDeliveryAmt !== null && (
                            <div className={styles['total-card']}>
                                <div className={styles['total-label']}>
                                    <span className={styles['total-icon']}>💰</span>
                                    배달료 합계
                                </div>
                                <div className={styles['total-amount']}>
                                    {formatAmount(totalDeliveryAmt)}
                                </div>
                            </div>
                        )}

                        {/* 배달 내역 카드 */}
                        <div className={styles['detail-card']}>
                            <h2 className={styles['section-title']}>
                                <span className={styles['section-title-icon']}>📋</span>
                                배달 내역
                            </h2>
                            <div className={styles['delivery-list']}>
                                {detailList.map((item, index) => (
                                    <div key={`${item.deliveryDate}-${index}`} className={styles['delivery-item']}>
                                        <div className={styles['item-header']}>
                                            <div className={styles['item-date']}>
                                                <span className={styles['item-date-icon']}>📅</span>
                                                {formatDate(item.deliveryDate)}
                                            </div>
                                            <span className={`${styles['status-badge']} ${getStatusBadgeClass(item.deliveryStatus)}`}>
                                                {getStatusIcon(item.deliveryStatus)} {item.deliveryStatus}
                                            </span>
                                        </div>
                                        <div className={styles['item-row']}>
                                            <span className={styles['item-label']}>배달료</span>
                                            <span className={`${styles['item-value']} ${styles['amount']}`}>
                                                {formatAmount(item.deliveryFee)}
                                            </span>
                                        </div>
                                        {item.faultReason && (
                                            <div className={styles['item-row']}>
                                                <span className={styles['item-label']}>귀책사유</span>
                                                <span className={styles['item-value']}>{item.faultReason}</span>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* 하단 네비게이션 */}
            <BottomNavigation currentScreen="delivery-history" />
        </div>
    );
};

export default DeliveryHistoryDetailPage;
